<template>
  <div class="analytics-page">
    <!-- Header -->
    <div class="header">
      <h1>Operasyon Analitiği</h1>
      <div class="filters">
        <input type="date" v-model="startDate" />
        <span>-</span>
        <input type="date" v-model="endDate" />
        <button @click="fetchAll" :disabled="loading">
          <i :class="loading ? 'fas fa-spinner fa-spin' : 'fas fa-search'"></i>
        </button>
      </div>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="loading">
      <div class="spinner"></div>
    </div>

    <div v-else class="content">
      <!-- Row 1: Main KPIs -->
      <div class="kpi-row">
        <div class="kpi-card primary">
          <div class="kpi-icon"><i class="fas fa-clipboard-list"></i></div>
          <div class="kpi-content">
            <span class="kpi-value">{{ totalOperations }}</span>
            <span class="kpi-label">Toplam Operasyon</span>
          </div>
        </div>
        <div class="kpi-card success">
          <div class="kpi-icon"><i class="fas fa-check-circle"></i></div>
          <div class="kpi-content">
            <span class="kpi-value">{{ summary?.spot?.completed || 0 }}</span>
            <span class="kpi-label">Tamamlanan</span>
            <span class="kpi-sub">%{{ completionRate }}</span>
          </div>
        </div>
        <div class="kpi-card danger">
          <div class="kpi-icon"><i class="fas fa-times-circle"></i></div>
          <div class="kpi-content">
            <span class="kpi-value">{{ summary?.spot?.cancelled || 0 }}</span>
            <span class="kpi-label">İptal</span>
            <span class="kpi-sub">%{{ cancellationRate }}</span>
          </div>
        </div>
        <div class="kpi-card info">
          <div class="kpi-icon"><i class="fas fa-truck"></i></div>
          <div class="kpi-content">
            <span class="kpi-value">{{ summary?.spot?.uniqueVehicles || 0 }}</span>
            <span class="kpi-label">Aktif Araç</span>
          </div>
        </div>
        <div class="kpi-card warning">
          <div class="kpi-icon"><i class="fas fa-user"></i></div>
          <div class="kpi-content">
            <span class="kpi-value">{{ summary?.spot?.uniqueDrivers || 0 }}</span>
            <span class="kpi-label">Aktif Sürücü</span>
          </div>
        </div>
      </div>

      <!-- Row 2: Secondary Stats -->
      <div class="secondary-row">
        <div class="stat-mini">
          <i class="fas fa-road"></i>
          <span class="val">{{ formatNumber(summary?.spot?.totalKilometers) }}</span>
          <span class="lbl">Toplam KM</span>
        </div>
        <div class="stat-mini">
          <i class="fas fa-weight-hanging"></i>
          <span class="val">{{ formatNumber(summary?.spot?.totalTonnage) }}</span>
          <span class="lbl">Toplam Tonaj</span>
        </div>
        <div class="stat-mini highlight-green" v-if="summary?.topPerformingVehicle">
          <i class="fas fa-trophy"></i>
          <span class="val">{{ summary.topPerformingVehicle.vehicle }}</span>
          <span class="lbl">En Aktif Araç ({{ summary.topPerformingVehicle.tripCount }} sefer)</span>
        </div>
        <div class="stat-mini highlight-red" v-if="summary?.topCancelledVehicle">
          <i class="fas fa-exclamation-triangle"></i>
          <span class="val">{{ summary.topCancelledVehicle.vehicle }}</span>
          <span class="lbl">En Çok İptal ({{ summary.topCancelledVehicle.cancelCount }} iptal)</span>
        </div>
      </div>

      <!-- Row 3: Distribution Charts -->
      <div class="charts-row">
        <!-- İş Türü Dağılımı -->
        <div class="chart-card">
          <div class="chart-header">
            <span>İş Türü Dağılımı</span>
            <span class="chart-total">Toplam: {{ totalOperations }}</span>
          </div>
          <div v-if="hasJobTypeData" class="dist-bars">
            <div class="dist-item">
              <div class="dist-info">
                <span class="dist-dot cyan"></span>
                <span class="dist-name">Spot</span>
                <span class="dist-count">{{ summary?.jobTypes?.spot || 0 }}</span>
              </div>
              <div class="dist-bar-wrap">
                <div class="dist-bar cyan" :style="{ width: spotRate + '%' }"></div>
              </div>
              <span class="dist-pct">%{{ spotRate }}</span>
            </div>
            <div class="dist-item">
              <div class="dist-info">
                <span class="dist-dot purple"></span>
                <span class="dist-name">Toplu</span>
                <span class="dist-count">{{ summary?.jobTypes?.toplu || 0 }}</span>
              </div>
              <div class="dist-bar-wrap">
                <div class="dist-bar purple" :style="{ width: topluRate + '%' }"></div>
              </div>
              <span class="dist-pct">%{{ topluRate }}</span>
            </div>
            <div class="dist-item">
              <div class="dist-info">
                <span class="dist-dot orange"></span>
                <span class="dist-name">Gemi Tahliyesi</span>
                <span class="dist-count">{{ summary?.jobTypes?.gemi || 0 }}</span>
              </div>
              <div class="dist-bar-wrap">
                <div class="dist-bar orange" :style="{ width: gemiRate + '%' }"></div>
              </div>
              <span class="dist-pct">%{{ gemiRate }}</span>
            </div>
          </div>
          <div v-else class="no-data">Veri yok</div>
        </div>

        <!-- İş Durumu -->
        <div class="chart-card">
          <div class="chart-header">
            <span>İş Durumu</span>
            <span class="chart-total">Spot: {{ summary?.spot?.totalOrders || 0 }}</span>
          </div>
          <div v-if="hasPieData" class="dist-bars">
            <div class="dist-item">
              <div class="dist-info">
                <span class="dist-dot green"></span>
                <span class="dist-name">Tamamlanan</span>
                <span class="dist-count">{{ summary?.spot?.completed || 0 }}</span>
              </div>
              <div class="dist-bar-wrap">
                <div class="dist-bar green" :style="{ width: completionRate + '%' }"></div>
              </div>
              <span class="dist-pct">%{{ completionRate }}</span>
            </div>
            <div class="dist-item">
              <div class="dist-info">
                <span class="dist-dot red"></span>
                <span class="dist-name">İptal</span>
                <span class="dist-count">{{ summary?.spot?.cancelled || 0 }}</span>
              </div>
              <div class="dist-bar-wrap">
                <div class="dist-bar red" :style="{ width: cancellationRate + '%' }"></div>
              </div>
              <span class="dist-pct">%{{ cancellationRate }}</span>
            </div>
            <div class="dist-item">
              <div class="dist-info">
                <span class="dist-dot blue"></span>
                <span class="dist-name">Devam Eden</span>
                <span class="dist-count">{{ ongoingCount }}</span>
              </div>
              <div class="dist-bar-wrap">
                <div class="dist-bar blue" :style="{ width: ongoingRate + '%' }"></div>
              </div>
              <span class="dist-pct">%{{ ongoingRate }}</span>
            </div>
          </div>
          <div v-else class="no-data">Veri yok</div>
        </div>

        <!-- En Aktif Araçlar -->
        <div class="chart-card">
          <div class="chart-header">
            <span>En Aktif Araçlar</span>
            <button @click="exportVehicles" title="Excel İndir"><i class="fas fa-file-excel"></i></button>
          </div>
          <apexchart v-if="vehicleData.length" :key="'vehicle-' + pieChartKey" type="bar" height="160" :options="vehicleChartOptions" :series="vehicleChartSeries" />
          <div v-else class="no-data">Veri yok</div>
        </div>

        <!-- En Aktif Sürücüler -->
        <div class="chart-card">
          <div class="chart-header">
            <span>En Aktif Sürücüler</span>
            <button @click="exportDrivers" title="Excel İndir"><i class="fas fa-file-excel"></i></button>
          </div>
          <apexchart v-if="driverData.length" :key="'driver-' + pieChartKey" type="bar" height="160" :options="driverChartOptions" :series="driverChartSeries" />
          <div v-else class="no-data">Veri yok</div>
        </div>
      </div>

      <!-- Row 4: Detail Tables -->
      <div class="tables-row">
        <div class="tbl-card">
          <div class="tbl-header">
            <span><i class="fas fa-truck"></i> Araç Performansı</span>
            <button @click="exportVehicles"><i class="fas fa-download"></i> Excel</button>
          </div>
          <table>
            <thead>
              <tr>
                <th>Plaka</th>
                <th class="center">Spot</th>
                <th class="center">Toplu</th>
                <th class="center">Toplam</th>
                <th class="center">Tamamlanan</th>
                <th class="center">İptal</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="v in vehicleData.slice(0, 8)" :key="v.plate">
                <td class="bold">{{ v.plate }}</td>
                <td class="center">{{ v.spotTrips || 0 }}</td>
                <td class="center">{{ v.bulkTrips || 0 }}</td>
                <td class="center bold">{{ v.totalTrips }}</td>
                <td class="center green">{{ v.completedTrips }}</td>
                <td class="center red">{{ v.cancelledTrips }}</td>
              </tr>
              <tr v-if="!vehicleData.length">
                <td colspan="6" class="center no-data-cell">Veri bulunamadı</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="tbl-card">
          <div class="tbl-header">
            <span><i class="fas fa-users"></i> Personel Performansı</span>
            <button @click="exportOperators"><i class="fas fa-download"></i> Excel</button>
          </div>
          <table>
            <thead>
              <tr>
                <th>İsim</th>
                <th class="center">Oluşturulan</th>
                <th class="center">Tamamlanan</th>
                <th class="center">İptal</th>
                <th class="center">Başarı %</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="o in operatorData.slice(0, 8)" :key="o.operatorId">
                <td class="bold">{{ truncate(o.operatorName || o.branchName, 20) }}</td>
                <td class="center">{{ o.totalCreated || o.totalAssignments }}</td>
                <td class="center green">{{ o.completed }}</td>
                <td class="center red">{{ o.cancelled }}</td>
                <td class="center bold">%{{ getSuccessRate(o) }}</td>
              </tr>
              <tr v-if="!operatorData.length">
                <td colspan="5" class="center no-data-cell">Veri bulunamadı</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import VueApexCharts from "vue-apexcharts";
import { analytics } from "@/networking/urlmanager";
import { saveAs } from "file-saver";
import * as XLSX from "xlsx";

export default {
  name: "OperationsAnalytics",
  components: { apexchart: VueApexCharts },
  data() {
    return {
      loading: false,
      startDate: this.getDefaultStartDate(),
      endDate: this.getDefaultEndDate(),
      summary: null,
      vehicleData: [],
      driverData: [],
      operatorData: [],
      pieChartKey: 0,
    };
  },
  computed: {
    totalOperations() {
      const jt = this.summary?.jobTypes || {};
      return (jt.spot || 0) + (jt.toplu || 0) + (jt.gemi || 0);
    },
    hasPieData() {
      const s = this.summary?.spot || {};
      return (s.totalOrders || 0) > 0;
    },
    hasJobTypeData() {
      return this.totalOperations > 0;
    },
    completionRate() {
      const s = this.summary?.spot || {};
      const total = s.totalOrders || 0;
      return total ? Math.round((s.completed || 0) / total * 100) : 0;
    },
    cancellationRate() {
      const s = this.summary?.spot || {};
      const total = s.totalOrders || 0;
      return total ? Math.round((s.cancelled || 0) / total * 100) : 0;
    },
    ongoingCount() {
      const s = this.summary?.spot || {};
      const total = s.totalOrders || 0;
      return Math.max(0, total - (s.completed || 0) - (s.cancelled || 0) - (s.returned || 0));
    },
    ongoingRate() {
      const s = this.summary?.spot || {};
      const total = s.totalOrders || 0;
      return total ? Math.round(this.ongoingCount / total * 100) : 0;
    },
    spotRate() {
      const jt = this.summary?.jobTypes || {};
      const total = jt.total || 0;
      return total ? Math.round((jt.spot || 0) / total * 100) : 0;
    },
    topluRate() {
      const jt = this.summary?.jobTypes || {};
      const total = jt.total || 0;
      return total ? Math.round((jt.toplu || 0) / total * 100) : 0;
    },
    gemiRate() {
      const jt = this.summary?.jobTypes || {};
      const total = jt.total || 0;
      return total ? Math.round((jt.gemi || 0) / total * 100) : 0;
    },
    vehicleChartOptions() {
      return {
        chart: { id: "vehicle-bar", type: "bar", toolbar: { show: false } },
        plotOptions: { bar: { horizontal: true, barHeight: "65%" } },
        colors: ["#3b82f6"],
        xaxis: { categories: this.vehicleData.slice(0, 5).map(v => v.plate) },
        yaxis: { labels: { style: { fontSize: "10px" } } },
        dataLabels: { enabled: true, style: { fontSize: "10px" } },
        grid: { padding: { left: 0, right: 10 } },
      };
    },
    vehicleChartSeries() {
      return [{ name: "Sefer", data: this.vehicleData.slice(0, 5).map(v => v.totalTrips) }];
    },
    driverChartOptions() {
      return {
        chart: { id: "driver-bar", type: "bar", toolbar: { show: false } },
        plotOptions: { bar: { horizontal: true, barHeight: "65%" } },
        colors: ["#22c55e"],
        xaxis: { categories: this.driverData.slice(0, 5).map(d => this.truncate(d.driverName, 10)) },
        yaxis: { labels: { style: { fontSize: "10px" } } },
        dataLabels: { enabled: true, style: { fontSize: "10px" } },
        grid: { padding: { left: 0, right: 10 } },
      };
    },
    driverChartSeries() {
      return [{ name: "Atama", data: this.driverData.slice(0, 5).map(d => d.totalAssignments) }];
    },
  },
  methods: {
    getDefaultStartDate() {
      const d = new Date();
      d.setMonth(d.getMonth() - 1);
      return d.toISOString().split("T")[0];
    },
    getDefaultEndDate() {
      return new Date().toISOString().split("T")[0];
    },
    truncate(str, len) {
      if (!str) return "";
      return str.length > len ? str.slice(0, len) + "…" : str;
    },
    formatNumber(num) {
      if (!num) return "0";
      return Number(num).toLocaleString("tr-TR");
    },
    getSuccessRate(o) {
      const total = o.totalCreated || o.totalAssignments || 0;
      const completed = o.completed || 0;
      return total ? Math.round(completed / total * 100) : 0;
    },
    async fetchAll() {
      this.loading = true;
      try {
        const headers = { Authorization: "Bareer " + this.$store.state.userData.token };
        const params = { startDate: this.startDate, endDate: this.endDate, page: 0, pageSize: 50 };

        const [summaryRes, vehicleRes, driverRes, operatorRes] = await Promise.all([
          axios.get(analytics.getSummary, { headers, params }),
          axios.get(analytics.getVehicleReport, { headers, params }),
          axios.get(analytics.getPersonnelReport, { headers, params: { ...params, type: "driver" } }),
          axios.get(analytics.getPersonnelReport, { headers, params: { ...params, type: "operator" } }),
        ]);

        this.summary = summaryRes.data.data;
        this.vehicleData = vehicleRes.data.data.detail || [];
        this.driverData = driverRes.data.data.detail || [];
        this.operatorData = operatorRes.data.data.detail || [];
        this.pieChartKey++;
      } catch (e) {
        this.errorBox(e.response);
      } finally {
        this.loading = false;
      }
    },
    exportData(data, headers, name) {
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.aoa_to_sheet([headers, ...data]);
      XLSX.utils.book_append_sheet(wb, ws, name);
      const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" });
      saveAs(new Blob([buf], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }), `${name}.xlsx`);
    },
    exportVehicles() {
      const headers = ["Plaka", "Spot", "Toplu", "Toplam", "Tamamlanan", "İptal", "Tonaj"];
      const data = this.vehicleData.map(v => [v.plate, v.spotTrips, v.bulkTrips, v.totalTrips, v.completedTrips, v.cancelledTrips, v.totalTonnage]);
      this.exportData(data, headers, "Arac-Raporu");
    },
    exportDrivers() {
      const headers = ["Sürücü", "TC", "Atama", "Tamamlanan", "İptal"];
      const data = this.driverData.map(d => [d.driverName, d.driverTC, d.totalAssignments, d.completed, d.cancelled]);
      this.exportData(data, headers, "Surucu-Raporu");
    },
    exportOperators() {
      const headers = ["Operatör", "Email", "Oluşturulan", "Tamamlanan", "İptal"];
      const data = this.operatorData.map(o => [o.operatorName || o.branchName, o.email, o.totalCreated || o.totalAssignments, o.completed, o.cancelled]);
      this.exportData(data, headers, "Operator-Raporu");
    },
  },
  mounted() {
    this.fetchAll();
  },
};
</script>

<style scoped>
.analytics-page {
  padding: 16px;
  background: #f8fafc;
  height: 100%;
  overflow: auto;
}

/* Header */
.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}
.header h1 { font-size: 18px; font-weight: 700; color: #1e293b; margin: 0; }
.filters { display: flex; gap: 8px; align-items: center; }
.filters input { padding: 6px 10px; border: 1px solid #e2e8f0; border-radius: 6px; font-size: 13px; background: white; }
.filters button { padding: 6px 12px; background: #3b82f6; color: white; border: none; border-radius: 6px; cursor: pointer; }
.filters button:hover { background: #2563eb; }
.filters button:disabled { opacity: 0.5; cursor: not-allowed; }

/* Loading */
.loading { display: flex; justify-content: center; padding: 60px; }
.spinner { width: 32px; height: 32px; border: 3px solid #e2e8f0; border-top-color: #3b82f6; border-radius: 50%; animation: spin 0.8s linear infinite; }
@keyframes spin { to { transform: rotate(360deg); } }

.content { display: flex; flex-direction: column; gap: 16px; }

/* KPI Row */
.kpi-row { display: grid; grid-template-columns: repeat(5, 1fr); gap: 12px; }
.kpi-card {
  display: flex;
  align-items: center;
  gap: 12px;
  background: white;
  border-radius: 10px;
  padding: 14px 16px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.06);
}
.kpi-icon {
  width: 42px;
  height: 42px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
}
.kpi-card.primary .kpi-icon { background: #eff6ff; color: #3b82f6; }
.kpi-card.success .kpi-icon { background: #f0fdf4; color: #22c55e; }
.kpi-card.danger .kpi-icon { background: #fef2f2; color: #ef4444; }
.kpi-card.info .kpi-icon { background: #f0f9ff; color: #0ea5e9; }
.kpi-card.warning .kpi-icon { background: #fffbeb; color: #f59e0b; }
.kpi-content { display: flex; flex-direction: column; }
.kpi-value { font-size: 22px; font-weight: 700; color: #1e293b; line-height: 1.2; }
.kpi-label { font-size: 11px; color: #64748b; text-transform: uppercase; letter-spacing: 0.5px; }
.kpi-sub { font-size: 11px; color: #94a3b8; margin-top: 2px; }

/* Secondary Row */
.secondary-row { display: flex; gap: 12px; flex-wrap: wrap; }
.stat-mini {
  display: flex;
  align-items: center;
  gap: 8px;
  background: white;
  border-radius: 8px;
  padding: 10px 14px;
  box-shadow: 0 1px 2px rgba(0,0,0,0.04);
}
.stat-mini i { color: #94a3b8; font-size: 14px; }
.stat-mini .val { font-size: 14px; font-weight: 600; color: #1e293b; }
.stat-mini .lbl { font-size: 11px; color: #64748b; }
.stat-mini.highlight-green { border-left: 3px solid #22c55e; }
.stat-mini.highlight-green i { color: #22c55e; }
.stat-mini.highlight-red { border-left: 3px solid #ef4444; }
.stat-mini.highlight-red i { color: #ef4444; }

/* Charts Row */
.charts-row { display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px; }
.chart-card {
  background: white;
  border-radius: 10px;
  padding: 14px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.06);
}
.chart-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  font-size: 12px;
  font-weight: 600;
  color: #475569;
}
.chart-header button {
  padding: 4px 8px;
  background: #f1f5f9;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  color: #64748b;
  font-size: 11px;
}
.chart-header button:hover { background: #e2e8f0; }
.chart-total { font-weight: 400; color: #94a3b8; font-size: 11px; }
.no-data { display: flex; align-items: center; justify-content: center; height: 120px; color: #94a3b8; font-size: 12px; }

/* Distribution Bars */
.dist-bars { display: flex; flex-direction: column; gap: 14px; }
.dist-item { display: flex; align-items: center; gap: 10px; }
.dist-info { display: flex; align-items: center; gap: 6px; min-width: 130px; }
.dist-dot { width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0; }
.dist-dot.green { background: #22c55e; }
.dist-dot.red { background: #ef4444; }
.dist-dot.blue { background: #3b82f6; }
.dist-dot.cyan { background: #06b6d4; }
.dist-dot.purple { background: #8b5cf6; }
.dist-dot.orange { background: #f59e0b; }
.dist-name { font-size: 11px; color: #475569; }
.dist-count { font-size: 11px; font-weight: 600; color: #1e293b; margin-left: auto; }
.dist-bar-wrap { flex: 1; height: 6px; background: #f1f5f9; border-radius: 3px; overflow: hidden; }
.dist-bar { height: 100%; border-radius: 3px; transition: width 0.4s ease; }
.dist-bar.green { background: #22c55e; }
.dist-bar.red { background: #ef4444; }
.dist-bar.blue { background: #3b82f6; }
.dist-bar.cyan { background: #06b6d4; }
.dist-bar.purple { background: #8b5cf6; }
.dist-bar.orange { background: #f59e0b; }
.dist-pct { font-size: 11px; font-weight: 600; color: #64748b; min-width: 32px; text-align: right; }

/* Tables Row */
.tables-row { display: grid; grid-template-columns: repeat(2, 1fr); gap: 12px; }
.tbl-card {
  background: white;
  border-radius: 10px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.06);
  overflow: hidden;
}
.tbl-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 14px;
  border-bottom: 1px solid #f1f5f9;
  font-size: 13px;
  font-weight: 600;
  color: #334155;
}
.tbl-header i { margin-right: 6px; color: #64748b; }
.tbl-header button {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 5px 10px;
  background: #f0fdf4;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  color: #22c55e;
  font-size: 11px;
  font-weight: 500;
}
.tbl-header button:hover { background: #dcfce7; }

table { width: 100%; border-collapse: collapse; }
th {
  padding: 8px 12px;
  text-align: left;
  font-size: 10px;
  font-weight: 600;
  color: #94a3b8;
  text-transform: uppercase;
  background: #fafafa;
  letter-spacing: 0.3px;
}
th.center, td.center { text-align: center; }
td {
  padding: 10px 12px;
  font-size: 12px;
  color: #475569;
  border-top: 1px solid #f1f5f9;
}
td.bold { font-weight: 600; color: #1e293b; }
td.green { color: #22c55e; font-weight: 500; }
td.red { color: #ef4444; font-weight: 500; }
tr:hover { background: #fafafa; }
.no-data-cell { color: #94a3b8; padding: 20px; }

/* Responsive */
@media (max-width: 1280px) {
  .kpi-row { grid-template-columns: repeat(3, 1fr); }
  .charts-row { grid-template-columns: repeat(2, 1fr); }
}
@media (max-width: 768px) {
  .kpi-row { grid-template-columns: 1fr 1fr; }
  .charts-row { grid-template-columns: 1fr; }
  .tables-row { grid-template-columns: 1fr; }
  .secondary-row { flex-direction: column; }
}

/* Dark mode */
.dark .analytics-page { background: #0f172a; }
.dark .header h1 { color: #f1f5f9; }
.dark .filters input { background: #1e293b; border-color: #334155; color: white; }
.dark .kpi-card { background: #1e293b; }
.dark .kpi-value { color: #f1f5f9; }
.dark .kpi-label { color: #94a3b8; }
.dark .stat-mini { background: #1e293b; }
.dark .stat-mini .val { color: #f1f5f9; }
.dark .chart-card { background: #1e293b; }
.dark .chart-header { color: #e2e8f0; }
.dark .dist-name { color: #cbd5e1; }
.dark .dist-count { color: #f1f5f9; }
.dark .dist-bar-wrap { background: #334155; }
.dark .dist-pct { color: #94a3b8; }
.dark .tbl-card { background: #1e293b; }
.dark .tbl-header { color: #e2e8f0; border-color: #334155; }
.dark th { background: #0f172a; color: #94a3b8; }
.dark td { border-color: #334155; color: #cbd5e1; }
.dark td.bold { color: #f1f5f9; }
.dark tr:hover { background: #334155; }
</style>
